<template>
  <div>
    <h4 class="m-8">{{ userData.name }} {{ userData.lastname }}</h4>
    <!-- Observador del Formulario -->
    <ValidationObserver ref="formObserver" v-slot="{ invalid, handleSubmit }">
      <b-form @submit.prevent="handleSubmit(onSubmit)">
        <!-- Primera Fila -->
        <b-row>
          <!-- Campo Name -->
          <b-col md="4">
            <ValidationProvider name="Name" rules="required" v-slot="{ errors, classes }">
              <b-form-group
                label="Name"
                label-for="name"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Name is required.">
                <b-form-input
                  id="name"
                  v-model="form.name"
                  :class="classes"
                  placeholder="Enter name"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Last Name -->
          <b-col md="4">
            <ValidationProvider
              name="Last Name"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Last Name"
                label-for="lastname"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Last Name is required.">
                <b-form-input
                  id="lastname"
                  v-model="form.lastname"
                  :class="classes"
                  placeholder="Enter last name"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Email -->
          <b-col md="4">
            <ValidationProvider
              name="Email"
              :rules="allFieldsRequired ? 'required|email' : 'required|email'"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Email"
                label-for="email"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Email is required and must be valid.">
                <b-form-input
                  id="email"
                  v-model="form.email"
                  :class="classes"
                  placeholder="Enter email"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- Segunda Fila -->
        <b-row>
          <!-- Campo Phone -->
          <b-col md="4">
            <ValidationProvider
              name="Phone"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Phone"
                label-for="phone"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Phone is required.">
                <b-form-input
                  id="phone"
                  v-model="form.phone"
                  :class="classes"
                  placeholder="Enter phone number"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Date of Birth -->
          <b-col md="4">
            <ValidationProvider
              name="Date of Birth"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Date of Birth"
                label-for="date_birth"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Date of Birth is required.">
                <flat-pickr
                  id="date_birth"
                  v-model="form.date_birth"
                  :config="config"></flat-pickr>
                <b-form-invalid-feedback v-if="errors.length">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Gender -->
          <b-col md="4">
            <ValidationProvider
              name="Gender"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Gender"
                label-for="gender"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Gender is required.">
                <b-form-select
                  id="gender"
                  v-model="form.gender"
                  :class="classes"
                  :options="genderOptions"></b-form-select>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- Tercera Fila -->
        <b-row>
          <!-- Campo Reference -->
          <b-col md="4">
            <ValidationProvider
              name="Reference"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Reference"
                label-for="reference"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Reference is required.">
                <treeselect
                  id="reference"
                  v-model="form.user_references_id"
                  :options="referenceOptions"
                  :normalizer="normalizer"
                  placeholder="Select reference"
                  :class="['custom-treeselect', { 'is-invalid': errors.length > 0 }]"></treeselect>
                <b-form-invalid-feedback v-if="errors.length">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Address -->
          <b-col md="4">
            <ValidationProvider
              name="Address"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Address"
                label-for="address"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Address is required.">
                <b-form-input
                  id="address"
                  v-model="form.address"
                  :class="classes"
                  placeholder="Enter address"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo City -->
          <b-col md="4">
            <ValidationProvider name="City" rules="required" v-slot="{ errors, classes }">
              <b-form-group
                label="City"
                label-for="city"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="City is required.">
                <treeselect
                  id="city"
                  v-model="form.cities_id"
                  :async="true"
                  :load-options="searchCitiesAsync"
                  :defaultOptions="initialCityOptions"
                  :normalizer="normalizer"
                  placeholder="Select city"
                  :class="['custom-treeselect', { 'is-invalid': errors.length > 0 }]"></treeselect>
                <b-form-invalid-feedback v-if="errors.length">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- Cuarta Fila -->
        <b-row>
          <!-- Campo Zip Code -->
          <b-col md="4">
            <ValidationProvider
              name="Zip Code"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Zip Code"
                label-for="zip_code"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Zip Code is required.">
                <b-form-input
                  id="zip_code"
                  v-model="form.zip_code"
                  :class="classes"
                  placeholder="Enter zip code"></b-form-input>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Status -->
          <b-col md="4">
            <ValidationProvider
              name="Status"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Status"
                label-for="status"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Status is required.">
                <b-form-select
                  id="status"
                  v-model="form.status"
                  :class="classes"
                  :options="statusOptions"></b-form-select>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <!-- Campo Member ID -->
          <b-col md="4">
            <ValidationProvider
              name="Member ID"
              :rules="allFieldsRequired ? 'required|max:255' : 'max:255'"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Member ID"
                label-for="member_id"
                :state="errors.length === 0 ? null : false"
                :invalid-feedback="allFieldsRequired ? 'Member ID is required.' : 'Member ID must be at most 255 characters.'">
                <b-form-input
                  id="member_id"
                  v-model="form.member_id"
                  :class="classes"
                  :maxlength="memberIdMaxLength"
                  placeholder="Enter member identifier"></b-form-input>
                <div class="d-flex justify-content-between mt-1">
                  <small class="text-muted">
                    {{ memberIdLength }} / {{ memberIdMaxLength }}
                  </small>
                  <small class="text-muted">
                    {{ memberIdRemaining }} {{ memberIdRemaining === 1 ? 'character left' : 'characters left' }}
                  </small>
                </div>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- Quinta Fila -->
        <b-row>
          <!-- Campo Insurance Agent -->
          <b-col md="4">
            <ValidationProvider
              name="Insurance Agent"
              :rules="allFieldsRequired && canEditInsuranceAgent ? 'required' : ''"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Insurance Agent"
                label-for="agent_insurance_id"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Insurance Agent is required.">
                <treeselect
                  id="agent_insurance_id"
                  v-model="form.agent_insurance_id"
                  :options="agentInsuranceOptions"
                  :normalizer="normalizer"
                  :disabled="!canEditInsuranceAgent"
                  placeholder="Select agent"
                  :class="['custom-treeselect', { 'is-invalid': errors.length > 0 }]"></treeselect>
                <small v-if="!canEditInsuranceAgent" class="text-muted d-block mt-1">
                  Only a super admin can change the insurance agent.
                </small>
                <b-form-invalid-feedback v-if="errors.length">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- Botón de Envío -->
        <b-button type="submit" :disabled="invalid" variant="primary"
          >Save Changes</b-button
        >
      </b-form>
    </ValidationObserver>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import ToastNotification from '@/components/ToastNotification'
import { extend } from 'vee-validate'
import { required, email, max as maxRule } from 'vee-validate/dist/rules'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import axiosC from '@/core/services/apiInsurance/admin/cities'
import axiosP from '@/core/services/apiInsurance/patient'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

// Reglas de validación
extend('required', required)
extend('email', email)
extend('max', maxRule)

export default {
  components: { flatPickr, ValidationObserver, ValidationProvider, ToastNotification,},
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        name: '',
        lastname: '',
        email: '',
        phone: '',
        date_birth: '',
        gender: '',
        user_references_id: null,
        address: '',
        cities_id: null,
        zip_code: '',
        status: '',
        member_id: '',
        agent_insurance_id: null,
      },
      referenceOptions: [],
      agentInsuranceOptions: [],
      initialCityOptions: [],
      statusOptions: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      genderOptions: [
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' },
      ],
      memberIdMaxLength: 255,
      config: {
        dateFormat: 'm-d-Y',
        allowInput: true,
      },
      boolFrontDesk: false,
      setTimeoutBuscador: null,
      allFieldsRequired: false,
      formInitialized: false,
    }
  },
  mounted() {
    if (JSON.parse(localStorage.getItem('userData')).roles[0].id === 5) {
      this.boolFrontDesk = true
    }
    if (this.userData && !this.formInitialized) {
      this.initializeForm()
      this.formInitialized = true
    }
    this.fetchReferences()
    this.loadInsuranceAgents()
    
    // Cargar ciudad inicial y luego verificar campos requeridos
    // Esperar a que todas las operaciones asíncronas y actualizaciones reactivas se completen
    Promise.resolve()
      .then(() => this.loadInitialCity())
      .then(() => {
        return new Promise(resolve => {
          // Esperar a que Vue procese todos los cambios reactivos
          this.$nextTick(() => {
            setTimeout(() => {
              resolve()
            }, 300)
          })
        })
      })
      .then(() => {
        // Verificar campos requeridos después de que todo esté completamente cargado
        if (this.formInitialized) {
          this.checkIfAllFieldsShouldBeRequired()
        }
      })
      .catch(error => {
        console.error('Error during initialization:', error)
        // Si hay error, aún así verificar los campos después de un delay
        setTimeout(() => {
          if (this.formInitialized) {
            this.checkIfAllFieldsShouldBeRequired()
          }
        }, 500)
      })
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.name,
      }
    },
    initializeForm() {
      try {
        // Convertimos el objeto reactivo a un objeto plano para evitar problemas de reactividad
        const userDataPlain = this.userData ? JSON.parse(JSON.stringify(this.userData)) : null

        if (userDataPlain) {
          // Manejar reference: puede venir como user_references_id o como reference
          let userReferencesId = null
          if (userDataPlain.user_references_id !== undefined && userDataPlain.user_references_id !== null) {
            userReferencesId = userDataPlain.user_references_id
          } else if (userDataPlain.reference !== undefined && userDataPlain.reference !== null) {
            userReferencesId = userDataPlain.reference
          }

          // Manejar cities_id: puede venir como cities_id directamente
          let citiesId = null
          if (userDataPlain.cities_id !== undefined && userDataPlain.cities_id !== null) {
            citiesId = userDataPlain.cities_id
          } else if (userDataPlain.city && typeof userDataPlain.city === 'number') {
            citiesId = userDataPlain.city
          }

          this.form = {
            name: userDataPlain.name || '',
            lastname: userDataPlain.lastname || '',
            email: userDataPlain.email || '',
            phone: userDataPlain.phone || '',
            date_birth: userDataPlain.date_birth || '',
            gender: userDataPlain.gender || '',
            user_references_id: userReferencesId,
            address: userDataPlain.address || '',
            cities_id: citiesId,
            zip_code: userDataPlain.zip_code || '',
            status: userDataPlain.status ?? true,
            member_id: userDataPlain.member_id || '',
            agent_insurance_id: userDataPlain.agent_insurance_id || null,
          }
        } else {
          // Valores por defecto si no hay datos
          this.form = {
            name: '',
            lastname: '',
            email: '',
            phone: '',
            date_birth: '',
            gender: '',
            user_references_id: null,
            address: '',
            cities_id: null,
            zip_code: '',
            status: true,
            member_id: '',
            agent_insurance_id: null,
          }
        }
      } catch (error) {
        console.error('Error en initializeForm:', error)
        // Valores por defecto en caso de error
        this.form = {
          name: '',
          lastname: '',
          email: '',
          phone: '',
          date_birth: '',
          gender: '',
          user_references_id: null,
          address: '',
          cities_id: null,
          zip_code: '',
          status: true,
          member_id: '',
          agent_insurance_id: null,
        }
      }
      
      // No verificar aquí para evitar verificaciones prematuras
      // La verificación se hará en mounted después de que loadInitialCity termine
    },
    checkIfAllFieldsShouldBeRequired() {
      // Verificar si alguno de los campos requeridos está vacío
      const hasEmptyName = !this.form.name || (typeof this.form.name === 'string' && this.form.name.trim() === '')
      const hasEmptyLastname = !this.form.lastname || (typeof this.form.lastname === 'string' && this.form.lastname.trim() === '')
      const hasEmptyEmail = !this.form.email || (typeof this.form.email === 'string' && this.form.email.trim() === '')
      const hasEmptyPhone = !this.form.phone || (typeof this.form.phone === 'string' && this.form.phone.trim() === '')
      const hasEmptyDateBirth = !this.form.date_birth || (typeof this.form.date_birth === 'string' && this.form.date_birth.trim() === '')
      const hasEmptyGender = !this.form.gender || (typeof this.form.gender === 'string' && this.form.gender.trim() === '')
      const hasEmptyReference = this.form.user_references_id === null || this.form.user_references_id === undefined
      const hasEmptyAddress = !this.form.address || (typeof this.form.address === 'string' && this.form.address.trim() === '')
      const hasEmptyCity = this.form.cities_id === null || this.form.cities_id === undefined
      const hasEmptyZipCode = !this.form.zip_code || (typeof this.form.zip_code === 'string' && this.form.zip_code.trim() === '')
      // El status puede ser true o false (ambos son válidos), solo está vacío si es null, undefined o cadena vacía
      const hasEmptyStatus = this.form.status === null || this.form.status === undefined || 
                            (typeof this.form.status === 'string' && this.form.status.trim() === '')
      
      // Si algún campo requerido está vacío, hacer que todos los campos sean requeridos
      this.allFieldsRequired = hasEmptyName || hasEmptyLastname || hasEmptyEmail || hasEmptyPhone || 
                               hasEmptyDateBirth || hasEmptyGender || hasEmptyReference || 
                               hasEmptyAddress || hasEmptyCity || hasEmptyZipCode || hasEmptyStatus
    },
    loadInsuranceAgents() {
      // Carga la lista de agentes de seguros
      axiosP
        .getInsuranceAgents()
        .then(response => {
          let rawAgents = []

          if (Array.isArray(response?.data?.data)) {
            rawAgents = response.data.data
          } else if (Array.isArray(response?.data)) {
            rawAgents = response.data
          } else if (Array.isArray(response)) {
            rawAgents = response
          }

          this.agentInsuranceOptions = rawAgents.map(agent => {
            const fullName = [agent?.name, agent?.lastname].filter(Boolean).join(' ').trim()
            return {
              id: agent?.id,
              name: fullName || agent?.name || agent?.lastname || '',
            }
          })
        })
        .catch(error => {
          console.error('Error loading insurance agents:', error)
          this.agentInsuranceOptions = []
        })
    },
    searchCitiesAsync({ action, searchQuery, callback }) {
      if (action === 'ASYNC_SEARCH') {
        clearTimeout(this.setTimeoutBuscador)
        const datos = { name: searchQuery || '' }
        this.setTimeoutBuscador = setTimeout(() => {
          axiosC
            .citiesFilterFilters(datos)
            .then(({ data }) => {
              callback(null, data || [])
            })
            .catch(error => {
              console.error('Error fetching cities:', error)
              callback(error, [])
            })
        }, 350)
      } else if (action === 'LOAD_CHILDREN_OPTIONS') {
        // Cargar opciones iniciales si hay una ciudad seleccionada
        if (this.initialCityOptions.length > 0) {
          callback(null, this.initialCityOptions)
        } else {
          callback(null, [])
        }
      }
    },
    async loadInitialCity() {
      // Cargar la ciudad inicial si existe cities_id
      if (this.userData?.cities_id) {
        // Si viene el objeto cities completo, agregarlo a las opciones iniciales
        if (this.userData.cities && this.userData.cities.id) {
          this.initialCityOptions = [
            {
              id: this.userData.cities.id || this.userData.cities_id,
              name: this.userData.cities.name || '',
            }
          ]
        } else {
          // Si solo viene el ID, buscar la ciudad en el API
          try {
            const cityId = this.userData.cities_id
            
            // Obtener todas las ciudades y filtrar por ID
            // Esto es más confiable que intentar buscar por ID directamente
            const allCitiesResponse = await axiosC.citiesList(1000)
            
            // La respuesta puede venir en diferentes formatos
            let allCities = []
            if (Array.isArray(allCitiesResponse?.data)) {
              allCities = allCitiesResponse.data
            } else if (Array.isArray(allCitiesResponse)) {
              allCities = allCitiesResponse
            } else if (allCitiesResponse?.data && Array.isArray(allCitiesResponse.data)) {
              allCities = allCitiesResponse.data
            }
            
            // Buscar la ciudad por ID
            const foundCity = allCities.find(city => city.id === cityId || city.id === Number(cityId))
            
            if (foundCity) {
              this.initialCityOptions = [
                {
                  id: foundCity.id,
                  name: foundCity.name || '',
                }
              ]
            } else {
              // Si no se encuentra, dejar vacío para que el usuario pueda buscar manualmente
              this.initialCityOptions = []
            }
          } catch (error) {
            console.error('Error loading city:', error)
            // Si hay error, dejar initialCityOptions vacío
            // El treeselect mostrará el ID y el usuario podrá buscar manualmente
            this.initialCityOptions = []
          }
        }
      } else {
        this.initialCityOptions = []
      }
      
      // Retornar una promesa resuelta para poder usar .then() en mounted
      return Promise.resolve()
    },
    fetchReferences() {
      // Llama al servicio para obtener referencias
      axiosP
        .referencePagination(100, 1)
        .then(({ data }) => {
          this.referenceOptions = (data.data || []).map(reference => ({
            id: reference.id,
            name: reference.name,
          }))
        })
        .catch(error => {
          console.error('Error fetching references:', error)
          this.referenceOptions = []
        })
    },
    onSubmit() {
      // Verificar campos requeridos antes de validar
      this.checkIfAllFieldsShouldBeRequired()
      
      // Maneja el envío del formulario
      this.$refs.formObserver.validate().then(valid => {
        if (valid) {
          if (!this.canEditInsuranceAgent) {
            this.form.agent_insurance_id = this.userData.agent_insurance_id || null
          }
          axiosP
            .patientUpdate(this.userData.id, this.form)
            .then(({ status }) => {
              if (status === 'ERROR') {
                this.$refs.toast.danger("Error updating user");
              } else {
                this.$refs.toast.success('User updated successfully')
                // Después de guardar exitosamente, verificar nuevamente los campos
                this.checkIfAllFieldsShouldBeRequired()
              }
            })
            .catch(() => {
              this.$refs.toast.danger('Error updating user')
            })
        }
      })
    },
  },
  computed: {
    canEditInsuranceAgent() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        const role = user.roles && user.roles[0]
        if (!role) return false
        return Number(role.id) === 1 || role.name === 'superadmin'
      } catch (error) {
        return false
      }
    },
    memberIdLength() {
      return (this.form.member_id || '').length
    },
    memberIdRemaining() {
      return this.memberIdMaxLength - this.memberIdLength
    },
  },
  watch: {
    // Observar cambios en userData para cargar la ciudad cuando esté disponible y verificar campos requeridos
    userData: {
      handler(newVal) {
        if (newVal && !this.formInitialized) {
          // Inicializar el formulario solo si aún no se ha inicializado
          this.initializeForm()
          this.formInitialized = true
          if (newVal.cities_id && this.initialCityOptions.length === 0) {
            this.loadInitialCity()
          }
        } else if (newVal && this.formInitialized) {
          // Si el formulario ya está inicializado, recargar ciudad si es necesario
          if (newVal.cities_id && this.initialCityOptions.length === 0) {
            this.loadInitialCity()
          }
        }
      },
      immediate: true,
      deep: true,
    },
    // Observar específicamente cities_id para recargar cuando cambie
    'userData.cities_id': {
      handler(newVal) {
        if (newVal && this.initialCityOptions.length === 0) {
          this.loadInitialCity()
        }
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.media-body {
  align-self: center;
}
.is-invalid {
  border-color: red;
}

.custom-treeselect {
  :deep(.vue-treeselect__control) {
    border-radius: 8px;
    border: 2px solid #e1e5e9;
    transition: all 0.3s ease;
    font-size: 14px;
    padding: 8px 12px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-height: 48px;
    
    &:hover {
      border-color: #007bff;
    }
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
  
  :deep(.vue-treeselect__placeholder) {
    color: #6c757d;
    font-style: italic;
  }
  
  :deep(.vue-treeselect__menu) {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e1e5e9;
    max-height: 200px;
    overflow-y: auto;
  }
  
  :deep(.vue-treeselect__option) {
    padding: 10px 16px;
    font-size: 14px;
    line-height: 1.4;
    transition: background-color 0.2s ease;
  }
  
  :deep(.vue-treeselect__option:hover) {
    background-color: #f8f9fa;
  }
  
  :deep(.vue-treeselect__option--selected) {
    background-color: #007bff;
    color: white;
  }
  
  :deep(.vue-treeselect__input) {
    font-size: 14px;
    padding: 4px 0;
  }
  
  :deep(.vue-treeselect__single-value) {
    font-size: 14px;
    color: #495057;
  }
}
</style>
